import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, fetchData, postData } from '../services/api';
import { PATH_SEGMENTS } from '../config/paths';
import { useLayout } from '../contexts/LayoutContext';
import SearchList, { SearchListColumn, SearchListConfig } from '../components/SearchList';
import { utteranceFilters } from '../config/filterConfigs';
import { usePagination } from '../hooks/usePagination';
import { FilterAction } from '../components/FilterBar';
import {
  UtteranceSourceRecord,
  UtteranceSourceSummary,
  UtteranceSearchResponse,
  UtteranceUploadResponse
} from '../types/api';
import '../styles/screens/UtteranceList.css';

const UtteranceListTable: React.FC = () => {
  const navigate = useNavigate();
  const { updateHeaderProps } = useLayout();

  const [sources, setSources] = useState<UtteranceSourceRecord[]>([]);
  const [summary, setSummary] = useState<UtteranceSourceSummary | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<Record<string, any>>({});
  const [searchQuery, setSearchQuery] = useState<string>('');

  const {
    paginationState,
    updatePagination,
    handlePageChange,
    handleSort
  } = usePagination(
    { disableAutoUrlUpdate: true },
    { field: 'sourcing_time', direction: 'desc' }
  );
  const updatePaginationRef = React.useRef(updatePagination);
  useEffect(() => {
    updatePaginationRef.current = updatePagination;
  }, [updatePagination]);

  useEffect(() => {
    updateHeaderProps({
      pageTitle: 'Utterance Sources',
      showInteractionExId: false
    });
  }, [updateHeaderProps]);

  const buildParams = useCallback((options: {
    filters: Record<string, any>;
    sort: { field: string; direction: 'asc' | 'desc' };
    pagination?: { page: number; per_page: number };
    allResults?: boolean;
  }): URLSearchParams => {
    const params = new URLSearchParams();

    if (options.allResults) {
      params.set('all_results', 'True');
    } else if (options.pagination) {
      params.set('page', options.pagination.page.toString());
      params.set('per_page', options.pagination.per_page.toString());
    }

    params.set('order_by', options.sort.field);
    params.set('order_direction', options.sort.direction);

    Object.entries(options.filters).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '') return;
      if (Array.isArray(value)) {
        value.forEach(v => params.append(key, v.toString()));
      } else {
        params.set(key, value.toString());
      }
    });

    return params;
  }, []);

  const fetchUtterancesFromAPI = useCallback(async (options: {
    filters: Record<string, any>;
    sort: { field: string; direction: 'asc' | 'desc' };
    pagination?: { page: number; per_page: number };
    allResults?: boolean;
  }) => {
    const params = buildParams(options);
    const url = `${api(PATH_SEGMENTS.UTTERANCE)}?${params.toString()}`;
    const response = await fetchData<UtteranceSearchResponse>(url);
    return response.data;
  }, [buildParams]);

  const loadUtterances = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const payload = await fetchUtterancesFromAPI({
        filters,
        sort: paginationState.sortConfig,
        pagination: {
          page: paginationState.currentPage,
          per_page: paginationState.itemsPerPage
        }
      });

      const sourceData = payload?.uttreance_sources;
      setSources(sourceData?.sources || []);
      setSummary(sourceData?.summary || null);

      if (payload?.pagination) {
        const nextTotalPages = payload.pagination.total_pages || 1;
        const nextItemsPerPage = payload.pagination.items_per_page || paginationState.itemsPerPage;

        if (
          nextTotalPages !== paginationState.totalPages ||
          nextItemsPerPage !== paginationState.itemsPerPage
        ) {
          updatePaginationRef.current({
            totalPages: nextTotalPages,
            itemsPerPage: nextItemsPerPage
          });
        }
      }
    } catch (err) {
      console.error('Error loading utterances:', err);
      setError(err instanceof Error ? err.message : 'Failed to load utterance sources');
    } finally {
      setLoading(false);
    }
  }, [fetchUtterancesFromAPI, filters, paginationState.currentPage, paginationState.itemsPerPage, paginationState.sortConfig]);

  useEffect(() => {
    loadUtterances();
  }, [loadUtterances]);

  const fetchAllResults = async (args: { filters: Record<string, any>; sort: { field: string; direction: 'asc' | 'desc' } }) => {
    try {
      const payload = await fetchUtterancesFromAPI({
        filters: args.filters,
        sort: args.sort,
        allResults: true
      });
      return payload?.uttreance_sources?.sources || [];
    } catch (err) {
      console.error('Error fetching all utterances:', err);
      return [];
    }
  };

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updatePagination({ currentPage: 1 });
    if (searchQuery.trim()) {
      setFilters(prev => ({ ...prev, name: searchQuery.trim() }));
      setSearchQuery('');
    }
  };

  const handleFilterChange = (newFilters: Record<string, any>) => {
    setFilters(newFilters);
    updatePagination({ currentPage: 1 });
  };

  const handleFilterActionChange = (_action: FilterAction) => {
    // SearchList handles filter action logic internally; parent just needs a stable callback.
  };

  const handleUpload = async (file: File, updateExisting: boolean) => {
    const formData = new FormData();
    formData.append('file', file);

    const params = new URLSearchParams();
    if (updateExisting) {
      params.set('update_existing', 'True');
    }

    const uploadUrl = params.toString()
      ? `${api(PATH_SEGMENTS.UTTERANCE, 'upload')}?${params.toString()}`
      : api(PATH_SEGMENTS.UTTERANCE, 'upload');

    const response = await postData<UtteranceUploadResponse>(
      uploadUrl,
      formData,
      'POST',
      'multipart/form-data'
    );

    const mappedErrors = (response.data?.errors || []).map(err => ({
      row: err.row,
      data: err.utterance ?? '',
      error: err.error
    }));

    return {
      message: response.message,
      data: {
        inserted_count: response.data?.inserted_count,
        updated_count: response.data?.updated_count,
        skipped_count: response.data?.skipped_count,
        failure_count: response.data?.failure_count,
        errors: mappedErrors
      }
    };
  };

  const handleUploadSuccess = () => {
    loadUtterances();
  };

  const handleSearchListSort = (field: string) => {
    handleSort(field);
  };

  const handleRowClick = (item: UtteranceSourceRecord) => {
    navigate(`/utterance/${item.id}`);
  };

  const columns: SearchListColumn[] = [
    {
      key: 'id',
      header: 'ID',
      sortable: true,
      width: '0.7fr',
      render: (item: UtteranceSourceRecord) => (
        <div className="utterance-id-cell">
          <span>{item.id}</span>
        </div>
      ),
      tooltip: (item: UtteranceSourceRecord) => `Source ID: ${item.id}`
    },
    {
      key: 'name',
      header: 'Name',
      sortable: true,
      width: '1.4fr',
      render: (item: UtteranceSourceRecord) => (
        <div className="utterance-name" title={item.name}>
          {item.name}
        </div>
      ),
      tooltip: (item: UtteranceSourceRecord) => item.name
    },
    {
      key: 'source',
      header: 'Source Tag',
      sortable: true,
      width: '1.2fr',
      render: (item: UtteranceSourceRecord) => (
        <span className="tag pill">{item.source || '—'}</span>
      ),
      tooltip: (item: UtteranceSourceRecord) => item.source || ''
    },
    {
      key: 'sourcing_time',
      header: 'Sourcing Time',
      sortable: true,
      width: '1.4fr',
      render: (item: UtteranceSourceRecord) => (
        <span>{item.sourcing_time ? new Date(item.sourcing_time).toLocaleString() : '—'}</span>
      ),
      tooltip: (item: UtteranceSourceRecord) =>
        item.sourcing_time ? new Date(item.sourcing_time).toLocaleString() : 'N/A'
    },
    {
      key: 'utterance_count',
      header: 'Utterances',
      sortable: true,
      width: '1fr',
      render: (item: UtteranceSourceRecord) => (
        <span className="count-chip">{item.utterance_count ?? 0}</span>
      )
    },
    {
      key: 'intent_annotation_count',
      header: 'Intents',
      sortable: true,
      width: '0.9fr',
      render: (item: UtteranceSourceRecord) => (
        <span className="count-chip neutral">{item.intent_annotation_count ?? 0}</span>
      )
    },
    {
      key: 'entity_annotation_count',
      header: 'Entities',
      sortable: true,
      width: '0.9fr',
      render: (item: UtteranceSourceRecord) => (
        <span className="count-chip neutral">{item.entity_annotation_count ?? 0}</span>
      )
    },
    {
      key: 'mmt_annotation_count',
      header: 'Moments',
      sortable: true,
      width: '0.9fr',
      render: (item: UtteranceSourceRecord) => (
        <span className="count-chip neutral">{item.mmt_annotation_count ?? 0}</span>
      )
    },
    {
      key: 'training_execution_count',
      header: 'Executions',
      sortable: true,
      width: '1fr',
      render: (item: UtteranceSourceRecord) => (
        <span className="count-chip highlight">{item.training_execution_count ?? 0}</span>
      )
    }
  ];

  const uploadConfigBase = {
    acceptedFileTypes: ['.csv'],
    title: 'Upload Utterance Source CSV',
    fileLabel: 'Select Utterance CSV',
    fileTypeDescription: 'CSV must include "utterance" header; other annotations optional',
    filenamePattern: /^[^\s-]+-[^\s-]+-\d{8,14}\.csv$/i,
    filenamePatternHelpText: 'Filename must follow name-source-YYYYMMDDHHMM.csv (last segment is 8-14 digits).',
  };

  const uploadInstructions = (
    <div>
      <p>{uploadConfigBase.filenamePatternHelpText}</p>
      <ul>
        <li><code>utterance</code> (required) – the utterance text.</li>
        <li><code>intents</code> – JSON array or comma separated names.</li>
        <li><code>entities</code> – JSON array of {'{ name, value }'} objects.</li>
        <li><code>mmts</code> – JSON array or comma separated moment names.</li>
      </ul>
      <p>Leave optional columns blank if those annotations are unavailable.</p>
    </div>
  );

  const searchListConfig: SearchListConfig = {
    columns,
    filterConfig: utteranceFilters,
    searchPlaceholder: 'Search sources by name',
    showSearch: true,
    showFilterButton: true,
    showFilterBar: true,
    showCreateButton: false,
    showBuildUpload: true,
    itemsPerPage: paginationState.itemsPerPage,
    defaultSort: { field: 'sourcing_time', direction: 'desc' },
    exportConfig: {
      enabled: true,
      filenameBase: 'utterance-sources'
    },
    uploadConfig: {
      ...uploadConfigBase,
      instructions: uploadInstructions,
    }
  };

  return (
    <div className="utterance-list-page">
      {summary && (
        <div className="utterance-summary-grid">
          <div className="summary-card">
            <span className="label">Total Sources</span>
            <strong>{summary.total_sources}</strong>
          </div>
          <div className="summary-card">
            <span className="label">Total Utterances</span>
            <strong>{summary.total_utterances}</strong>
          </div>
          <div className="summary-card">
            <span className="label">Returned Sources</span>
            <strong>{summary.returned_sources}</strong>
          </div>
          <div className="summary-card">
            <span className="label">Training Executions</span>
            <strong>{summary.total_training_executions}</strong>
          </div>
        </div>
      )}

      <SearchList
        config={searchListConfig}
        data={sources}
        loading={loading}
        error={error}
        filters={filters}
        searchQuery={searchQuery}
        currentPage={paginationState.currentPage}
        totalPages={paginationState.totalPages}
        itemsPerPage={paginationState.itemsPerPage}
        totalItems={summary?.total_sources}
        sortConfig={paginationState.sortConfig}
        onSearchChange={handleSearchChange}
        onSearchSubmit={handleSearchSubmit}
        onFilterChange={handleFilterChange}
        onFilterActionChange={handleFilterActionChange}
        onPageChange={handlePageChange}
        onSort={handleSearchListSort}
        onItemClick={handleRowClick}
        onUpload={handleUpload}
        onUploadSuccess={handleUploadSuccess}
        emptyMessage="No utterance sources found"
        loadingMessage="Loading utterance sources..."
        fetchAllResults={fetchAllResults}
      />
    </div>
  );
};

export default UtteranceListTable;

